import { cn } from "@/common/helpers/utils";
import { Button, ButtonSize, ButtonVariant } from "@/components/ui/button";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from "@/components/ui/command";
import { Loader, LoaderVariant } from "@/components/ui/loader";
import { SearchIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { KeyboardKey } from "./_components/keyboard-key";
import {
  COMMAND_PALETTE_KEYBOARD_SHORTCUT,
  getModifierKeyLabel,
  isMacPlatform,
  isModifierShortcut,
} from "./_helpers/keyboard";
import { scoreSearchTerms, toSearchTerms } from "./_helpers/score-command";
import { useCommandGroups } from "./_hooks/use-command-groups";
import type { PaletteCommand } from "./types";

export function CommandPalette() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const modifierKey = getModifierKeyLabel(t);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.repeat || !isModifierShortcut(event, COMMAND_PALETTE_KEYBOARD_SHORTCUT)) return;

      event.preventDefault();
      setOpen((isOpen) => !isOpen);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <>
      <Button
        type="button"
        variant={ButtonVariant.Input}
        size={ButtonSize.Sm}
        onClick={() => setOpen(true)}
        aria-label={t("CommandPaletteOpen")}
        aria-keyshortcuts={isMacPlatform() ? "Meta+K" : "Control+K"}
        className="text-muted-foreground h-9 md:w-64 md:justify-start lg:w-80"
      >
        <SearchIcon />
        <span className="hidden font-normal md:inline">{t("SearchLabel")}</span>
        <span className="ml-auto hidden gap-0.5 md:inline-flex">
          <KeyboardKey>{modifierKey}</KeyboardKey>
          <KeyboardKey>{COMMAND_PALETTE_KEYBOARD_SHORTCUT.toUpperCase()}</KeyboardKey>
        </span>
      </Button>
      <CommandDialog
        open={open}
        onOpenChange={setOpen}
        title={t("CommandPaletteTitle")}
        description={t("CommandPaletteDescription")}
        className={cn(
          "top-[12vh] translate-y-0 sm:max-w-xl md:max-w-2xl",
          "bg-popover/60 shadow-2xl ring-1 ring-foreground/10 ring-inset backdrop-blur-2xl backdrop-saturate-200",
          "border-foreground/10 dark:bg-linear-to-b dark:from-white/8 dark:to-transparent dark:shadow-black/50",
          "[@media(prefers-reduced-transparency:reduce)]:bg-popover [@media(prefers-reduced-transparency:reduce)]:bg-none",
        )}
        overlayClassName="bg-black/10 backdrop-blur-none"
        commandProps={{
          filter: (_value, search, terms) => scoreSearchTerms(search, terms),
          loop: true,
          className: "bg-transparent",
        }}
      >
        <CommandPaletteContent onClose={() => setOpen(false)} />
      </CommandDialog>
    </>
  );
}

function CommandPaletteContent({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const [search, setSearch] = useState("");
  const groups = useCommandGroups(search);
  // cmdk only counts items it matched itself, so API results and pending searches must suppress the empty state.
  const hasExternalResults = groups.some(
    (group) => group.isLoading || (group.isPreFiltered && group.commands.length > 0),
  );

  const runCommand = (command: PaletteCommand) => {
    onClose();

    if ("to" in command) {
      navigate(`~${command.to}`);
    } else {
      command.perform();
    }
  };

  return (
    <>
      <CommandInput
        value={search}
        onValueChange={setSearch}
        placeholder={t("CommandPalettePlaceholder")}
        wrapperClassName="border-foreground/10 rounded-none focus-within:ring-0"
      />
      <CommandList className="max-h-[min(60vh,28rem)]">
        {!hasExternalResults && <CommandEmpty>{t("NoResultsFound")}</CommandEmpty>}
        {groups.map((group) => (
          <CommandGroup key={group.id} heading={group.heading} forceMount={group.isPreFiltered}>
            {group.isLoading && (
              <Loader variant={LoaderVariant.Wheel} className="py-3" aria-label={t("CommandPaletteSearching")} />
            )}
            {group.commands.map((command) => (
              <CommandItem
                key={command.id}
                value={`${group.id}:${command.id}`}
                keywords={toSearchTerms(command)}
                forceMount={group.isPreFiltered}
                onSelect={() => runCommand(command)}
                className="data-[selected=true]:bg-foreground/10"
              >
                {command.icon}
                <span className="truncate">{command.title}</span>
                {command.subtitle && <span className="text-muted-foreground truncate text-xs">{command.subtitle}</span>}
                {command.shortcut && (
                  <CommandShortcut className="flex gap-0.5">
                    {command.shortcut.map((key) => (
                      <KeyboardKey key={key}>{key}</KeyboardKey>
                    ))}
                  </CommandShortcut>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
      <div className="border-foreground/10 text-muted-foreground hidden items-center gap-4 border-t px-3 py-2 text-xs sm:flex">
        <span className="flex items-center gap-1">
          <KeyboardKey>↑</KeyboardKey>
          <KeyboardKey>↓</KeyboardKey>
          {t("CommandPaletteNavigateHint")}
        </span>
        <span className="flex items-center gap-1">
          <KeyboardKey>↵</KeyboardKey>
          {t("CommandPaletteSelectHint")}
        </span>
        <span className="ml-auto flex items-center gap-1">
          <KeyboardKey>Esc</KeyboardKey>
          {t("CommandPaletteCloseHint")}
        </span>
      </div>
    </>
  );
}
